import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  EnvelopeIcon, 
  UserIcon, 
  PhoneIcon, 
  LockClosedIcon, 
  BuildingOfficeIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import api from '@/services/api';
import { useLanguage } from '@/state/language/LanguageProvider';
import { PublicHeader } from '@/components/public/PublicHeader';

interface Company {
  id: string;
  name: string;
  domain?: string;
}

export const EmailConfirmationPage: React.FC = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    password: '',
    confirmPassword: '',
    companyName: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isSearchingCompanies, setIsSearchingCompanies] = useState(false);

  // Check if token is valid on component mount
  useEffect(() => {
    if (!token) {
      setIsTokenValid(false);
      toast.error(t('auth.invalidConfirmationLink'));
      return;
    }
    setIsTokenValid(true);
  }, [token]);

  // Search companies as user types
  useEffect(() => {
    const searchCompanies = async () => {
      if (formData.companyName.length < 2) {
        setCompanies([]);
        setShowCompanySuggestions(false);
        return;
      }

      setIsSearchingCompanies(true);
      try {
        const response = await api.get(`/tenants/search?q=${encodeURIComponent(formData.companyName)}`);
        setCompanies(response.data);
        setShowCompanySuggestions(response.data.length > 0);
      } catch (error) {
        console.error('Error searching companies:', error);
        setCompanies([]);
        setShowCompanySuggestions(false);
      } finally {
        setIsSearchingCompanies(false);
      }
    };

    const debounceTimer = setTimeout(searchCompanies, 300);
    return () => clearTimeout(debounceTimer);
  }, [formData.companyName]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear selected company when user starts typing again
    if (field === 'companyName') {
      setSelectedCompany(null);
    }
  };

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
    setFormData(prev => ({ ...prev, companyName: company.name }));
    setShowCompanySuggestions(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth.passwordsDoNotMatch'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }

    if (!token) {
      toast.error(t('auth.invalidConfirmationToken'));
      return;
    }

    setIsLoading(true);

    try {
      const registrationData = {
        token,
        name: formData.name,
        phone: formData.phone,
        password: formData.password,
        companyName: formData.companyName,
        existingCompanyId: selectedCompany?.id || null
      };

      await api.post('/auth/register/confirm', registrationData);
      
      toast.success(t('auth.accountCreatedSuccessfully'));
      navigate('/');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('auth.registrationFailed');
      toast.error(errorMessage);
      
      // If token is expired, offer to resend
      if (error.response?.status === 400 && errorMessage.includes('expired')) {
        if (window.confirm(t('auth.confirmationLinkExpired'))) {
          navigate('/register');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking token
  if (isTokenValid === null) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex flex-col">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-primary-bg)] mx-auto mb-4"></div>
            <p className="text-[var(--color-surface-text)]">{t('auth.validatingConfirmation')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if token is invalid
  if (isTokenValid === false) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex flex-col">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="text-center max-w-md w-full bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-8">
            <div className="w-20 h-20 bg-[var(--color-error)] rounded-full flex items-center justify-center mx-auto mb-4">
              <EnvelopeIcon className="h-10 w-10 text-[var(--color-accent-text)]" />
            </div>
            <h1 className="text-2xl font-bold text-[var(--color-primary-bg)] mb-4">
              {t('auth.invalidConfirmationLinkTitle')}
            </h1>
            <p className="text-[var(--color-surface-text)] mb-6">
              {t('auth.invalidConfirmationLinkDescription')}
            </p>
            <button
              onClick={() => navigate('/register')}
              className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-colors"
            >
              {t('auth.registerAgain')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)] flex flex-col">
      <PublicHeader />

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-8">
            {/* Success Message */}
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-[var(--color-primary-bg)] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckIcon className="h-10 w-10 text-[var(--color-primary-text)]" />
              </div>
              <h1 className="text-3xl font-bold text-[var(--color-primary-bg)] mb-2">
                {t('auth.emailConfirmed')}
              </h1>
              <p className="text-lg text-[var(--color-surface-text)]">
                {t('auth.completeRegistration')}
              </p>
            </div>

            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Field */}
              <div className="flex items-center space-x-3">
                <UserIcon className="h-5 w-5 text-[var(--color-primary-bg)] flex-shrink-0" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={t('auth.enterFullName')}
                  className="flex-1 py-3 px-3 border-2 border-[var(--color-primary-bg)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] placeholder-[var(--color-secondary-text)] opacity-75 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent text-center"
                  required
                />
              </div>

              {/* Phone Field */}
              <div className="flex items-center space-x-3">
                <PhoneIcon className="h-5 w-5 text-[var(--color-primary-bg)] flex-shrink-0" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder={t('auth.enterPhoneNumber')}
                  className="flex-1 py-3 px-3 border-2 border-[var(--color-primary-bg)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] placeholder-[var(--color-secondary-text)] opacity-75 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent text-center"
                  required
                />
              </div>

              {/* Company Field */}
              <div className="relative">
                <div className="flex items-center space-x-3">
                  <BuildingOfficeIcon className="h-5 w-5 text-[var(--color-primary-bg)] flex-shrink-0" />
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    placeholder={t('auth.enterCompanyName')}
                    className="flex-1 py-3 px-3 border-2 border-[var(--color-primary-bg)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] placeholder-[var(--color-secondary-text)] opacity-75 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent text-center"
                    required
                  />
                </div>

                {/* Company Suggestions */}
                {showCompanySuggestions && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                    {isSearchingCompanies ? (
                      <div className="p-3 text-[var(--color-secondary-text)] text-sm">Searching companies...</div>
                    ) : (
                      companies.map((company) => (
                        <button
                          key={company.id}
                          type="button"
                          onClick={() => handleCompanySelect(company)}
                          className="w-full p-3 text-left text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)] border-b border-[var(--color-border)] last:border-b-0 flex items-center justify-between"
                        >
                          <span>{company.name}</span>
                          <CheckIcon className="h-4 w-4 text-[var(--color-primary-bg)]" />
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Password Field */}
              <div className="flex items-center space-x-3">
                <LockClosedIcon className="h-5 w-5 text-[var(--color-primary-bg)] flex-shrink-0" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder={t('auth.createPassword')}
                  className="flex-1 py-3 px-3 border-2 border-[var(--color-primary-bg)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] placeholder-[var(--color-secondary-text)] opacity-75 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent text-center"
                  required
                />
              </div>

              {/* Confirm Password Field */}
              <div className="flex items-center space-x-3">
                <LockClosedIcon className="h-5 w-5 text-[var(--color-primary-bg)] flex-shrink-0" />
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder={t('auth.confirmPassword')}
                  className="flex-1 py-3 px-3 border-2 border-[var(--color-primary-bg)] rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] placeholder-[var(--color-secondary-text)] opacity-75 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent text-center"
                  required
                />
              </div>

              {/* Create Account Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] py-3 px-4 rounded-lg font-semibold text-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
              </button>
            </form>
          </div>
        </div>
      </div>
      
    </div>
  );
};
